import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
  ModalTrigger,
} from '@synoptic/ui-kit/modal/modal.js';
import { ModalStepper } from '@synoptic/ui-kit/modal/modal.tsx';
import { FC, ReactNode, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CustomTargetsDataInput } from '@/__generated__/graphql.ts';
import { CustomStreamForm } from '@/components/streams/stream-form/custom-stream-form/custom-stream-form.tsx';
import { RegularStreamForm } from '@/components/streams/stream-form/regular-stream-form/regular-stream-form.tsx';
import {
  StreamForm,
  StreamFormSubmitValues,
} from '@/components/streams/stream-form/stream-form.tsx';
import { StreamPricingType } from '@/components/streams/stream-pricing-type.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { deleteSearchParams } from '@/utils/handle-search-param.ts';
import { useAddStream } from './use-add-stream.ts';

export type NewStreamModalProps = {
  trigger: ReactNode;
  customStream?: boolean;
};

export const NewStreamModal: FC<NewStreamModalProps> = ({
  trigger,
  customStream,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stripeAccountVerified = searchParams.has('stripeAccountVerified');

  const [open, onOpenChange] = useState(stripeAccountVerified);
  const [addStream] = useAddStream();

  const [streamPricingType, setStreamPricingType] = useState<StreamPricingType>(
    searchParams.has('stripeAccountVerified') || customStream
      ? StreamPricingType.Paid
      : StreamPricingType.Free,
  );

  const [step, setStep] = useState<number>(1);

  const onSubmit = async (input: StreamFormSubmitValues) => {
    const { data } = await addStream({
      ...input,
      customTargetsData: input.customTargetsData
        ? ({
            ...input.customTargetsData,
          } as CustomTargetsDataInput)
        : undefined,
    });

    if (data) {
      navigate(paths.makeStream(data.addStream.id));
    }
  };

  const onClose = () => {
    onOpenChange(false);
    setStep(1);

    if (stripeAccountVerified)
      setSearchParams(
        deleteSearchParams(searchParams, ['stripeAccountVerified']),
        {
          replace: true,
        },
      );
  };

  return (
    <ModalRoot
      open={open}
      onOpenChange={(open) => {
        if (open) onOpenChange(open);
        else onClose();
      }}
    >
      <ModalTrigger asChild>{trigger}</ModalTrigger>
      <ModalContent>
        <ModalHeader>
          <div>
            <ModalStepper
              step={step}
              totalSteps={
                streamPricingType === StreamPricingType.Paid || customStream
                  ? 3
                  : 2
              }
            />
            <ModalTitle>Create new stream</ModalTitle>
          </div>
        </ModalHeader>

        <StreamForm
          onSubmit={onSubmit}
          pricingType={streamPricingType}
          customStream={customStream}
        >
          {!customStream ? (
            <RegularStreamForm
              step={step}
              setStep={setStep}
              onClose={onClose}
              streamPricingType={streamPricingType}
              setStreamPricingType={setStreamPricingType}
            />
          ) : (
            <CustomStreamForm step={step} setStep={setStep} onClose={onClose} />
          )}
        </StreamForm>
      </ModalContent>
    </ModalRoot>
  );
};
